/**
 * Toon ramps (ART-BIBLE §2.1).
 *
 * 256x1 DataTextures, sampled by MeshToonMaterial at (dotNL * 0.5 + 0.5).
 * Two things make these *anime* rather than "flat-shaded 3D":
 *   1. the softness of each step is authored per ramp (in texels), not global;
 *   2. the ramp is COLOURED — the shadow band is hue-shifted cool-violet and the
 *      lit band warm, so light itself has a temperature.
 *
 * Values stay in 0..1 (UnsignedByte, NoColorSpace = raw linear multipliers) so we never
 * depend on float-texture support: brightness headroom comes from light intensity instead.
 */

import { DataTexture, LinearFilter, ClampToEdgeWrapping, RGBAFormat, UnsignedByteType, NoColorSpace, type Texture } from 'three';

export interface RampSpec {
  /** band multipliers, dark → light */
  values: number[];
  /** thresholds in x (= dotNL*0.5+0.5); values.length - 1 entries */
  steps: number[];
  /** transition width in texels for each step (1 = razor, 12 = soft) */
  soft: number | number[];
}

/** cool shadows / warm lights — the temperature ramp applied across the bands */
const COOL: [number, number, number] = [0.80, 0.85, 1.08];
const WARM: [number, number, number] = [1.05, 1.01, 0.94];

function tintFor(bandT: number): [number, number, number] {
  return [
    COOL[0] + (WARM[0] - COOL[0]) * bandT,
    COOL[1] + (WARM[1] - COOL[1]) * bandT,
    COOL[2] + (WARM[2] - COOL[2]) * bandT,
  ];
}

const smoothstep = (a: number, b: number, x: number) => {
  if (b <= a) return x < a ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export function makeRamp(spec: RampSpec, width = 256): DataTexture {
  const { values, steps } = spec;
  const soft = Array.isArray(spec.soft) ? spec.soft : steps.map(() => spec.soft as number);
  const data = new Uint8Array(width * 4);

  for (let i = 0; i < width; i++) {
    const x = (i + 0.5) / width;
    // resolve band as a soft-mixed index
    let v = values[0];
    let tint = tintFor(0);
    for (let s = 0; s < steps.length; s++) {
      const half = Math.max(0.5, soft[s]) / width / 2;
      const t = smoothstep(steps[s] - half, steps[s] + half, x);
      if (t > 0) {
        const nextT = (s + 1) / Math.max(1, values.length - 1);
        const prevT = s / Math.max(1, values.length - 1);
        v = v + (values[s + 1] - v) * t;
        const a = tintFor(prevT), b = tintFor(nextT);
        tint = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
      }
    }
    const o = i * 4;
    data[o + 0] = Math.min(255, Math.round(v * tint[0] * 255));
    data[o + 1] = Math.min(255, Math.round(v * tint[1] * 255));
    data[o + 2] = Math.min(255, Math.round(v * tint[2] * 255));
    data[o + 3] = 255;
  }

  const tex = new DataTexture(data, width, 1, RGBAFormat, UnsignedByteType);
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.colorSpace = NoColorSpace;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

let _cache: Record<string, DataTexture> | null = null;

/** the four sanctioned ramps — everything in the game uses one of these */
export function ramps(): Record<RampName, Texture> {
  if (!_cache) {
    _cache = {
      HARD3: makeRamp({ values: [0.30, 0.62, 1.0], steps: [0.505, 0.66], soft: 2 }),
      SOFT2: makeRamp({ values: [0.46, 1.0], steps: [0.52], soft: 8 }),
      METAL4: makeRamp({ values: [0.18, 0.44, 0.76, 1.0], steps: [0.44, 0.58, 0.78], soft: 1 }),
      CLOTH3: makeRamp({ values: [0.36, 0.68, 1.0], steps: [0.46, 0.68], soft: 12 }),
    };
  }
  return _cache as Record<RampName, Texture>;
}

export type RampName = 'HARD3' | 'SOFT2' | 'METAL4' | 'CLOTH3';
